import React, { useMemo } from 'react';
import { Box, Inscription, LinkButton, ShimmerBox } from '@common-fe/common-fe';

import { OrganizationTypes } from '@/common/constants';
import { BankingBalanceDetailItem } from '@/modules/employer/components/BankingBalance/types';

import { Status } from '../../../BankingBalanceList/BankingBalanceListItem';

import BankingBalanceDetailRow from './BankingBalanceDetailRow';
import BankingBalanceOrgOptionalFields from './BankingBalanceOrgOptionalFields';

interface Props {
  data?: BankingBalanceDetailItem;
  isLoading?: boolean;
  onSeeIncomingPayments?: () => void;
}

const HINT_TEXT = `Balance shows the net funding balance based on recorded account activity, including deposits, spending,
 transfers, and adjustments. This amount is transaction-based and may differ 
 from the physical bank balance due to activity processed within the system outside of banking hours.`;

const BankingBalanceOrgDetailsCard: React.FC<Props> = ({ data, isLoading, onSeeIncomingPayments }) => {
  const isOptionalFieldsVisible = useMemo(
    () => data?.orgType !== OrganizationTypes.system,
    [data?.orgType]
  );

  return (
    <Box
      pad="spacing24"
      background={{ color: 'canvas' }}
      round="container1Round"
      border={{ color: 'border2' }}
    >
      <Box margin={{ bottom: 'spacing12' }} height="40px" align="center" direction="row">
        <Inscription size="20px" lineHeight="28px">
          Organization Details
        </Inscription>
      </Box>

      {isLoading ? (
        <ShimmerBox width="100%" height="265px" />
      ) : 
      (
        <Box direction="row" height="fit-content">
          <Box flex gap="spacing4">
            <Box height="24px" direction="row" align="center" justify="between">
              <Inscription lineHeight="20px">Status</Inscription>
              <Status status={data?.status || ''} />
            </Box>
            <BankingBalanceDetailRow
              testId="org-details_hsaBalance"
              label="HSA balance"
              value={data?.hsaBalance}
              isBold
              hintText={HINT_TEXT}
            />
            <BankingBalanceDetailRow
              testId="org-details_notionalBalance"
              label="Notional balance"
              value={data?.notionalBalance}
              isBold
              hintText={HINT_TEXT}
            />
            {onSeeIncomingPayments && (
              <Box border={{ side: 'top', color: 'border2' }} pad={{ top: 'spacing8' }}>
                <LinkButton testId="org-details_see-incoming-payments" onClick={onSeeIncomingPayments} linkInlineMode>
                  See Incoming Payments History
                </LinkButton>
              </Box>
            )}
          </Box>

          <Box
            width="1px"
            height={{ min: 'min-content' }}
            background={{ color: 'border2' }}
            margin={{ horizontal: 'spacing24' }}
          />

          {isOptionalFieldsVisible ? (
            <Box flex>
              <BankingBalanceOrgOptionalFields data={data} />
            </Box>
          ) : (
            <Box flex />
          )}
        </Box>)}
    </Box>
  );
};

export default BankingBalanceOrgDetailsCard;
